var _excluded = ["day", "onChangeFocusedDate", "getClosestDateToFocus", "aria-colindex"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { format, isEqual, isToday } from 'date-fns';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useIntl } from '../../../../../theme/i18n';
import { useMultiStyleConfig } from '../../../../../utils/use-style-config';
import { Box } from '../../../Box';
import { useCalendarContext } from '../../CalendarContext';
import { useFocus, useKeyDown } from '../../hooks';
import { getTargetDayDateByArrowKeyType } from '../../utilities';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var Day = /*#__PURE__*/forwardRef((_ref, forwardRef) => {
  var {
    day,
    onChangeFocusedDate,
    getClosestDateToFocus,
    ['aria-colindex']: ariaColIndex
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var {
    size,
    showToday,
    isDisabled: isCalenderDisabled,
    isMobileDatePicker,
    focusableDate,
    focusedDate,
    weekDays
  } = useCalendarContext();
  var styles = useMultiStyleConfig('_BaseCalendar', {
    isMobileDatePicker,
    size
  });
  var {
    formatDate
  } = useIntl();
  var ref = useRef(null);
  useImperativeHandle(forwardRef, () => ref.current);
  var isFocused = useMemo(() => Boolean(focusedDate && day && isEqual(focusedDate, day.date)), [focusedDate, day]);
  var handleArrowKey = useCallback(key => {
    if (!day) return;
    var targetDate = getTargetDayDateByArrowKeyType(key, day.date, weekDays);

    if (targetDate) {
      getClosestDateToFocus(targetDate);
    }
  }, [day, getClosestDateToFocus, weekDays]);
  var {
    onKeyDown
  } = useKeyDown({
    handleArrowKey,
    handleSelectKey: () => day === null || day === void 0 ? void 0 : day.selectDay()
  });
  var {
    onFocus,
    onBlur
  } = useFocus({
    ref,
    isFocused,
    handleOnFocus: () => {
      onChangeFocusedDate(day === null || day === void 0 ? void 0 : day.date);
    },
    handleOnBlur: () => {
      onChangeFocusedDate(undefined);
    }
  }); // For the empty slots in the month selection (say, the 1st of the month is starting on Monday and not Sunday).

  if (!day) {
    return /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({}, props), {}, {
      role: "gridcell",
      ref: ref
    }));
  }

  var isFocusableDate = !isCalenderDisabled && (focusedDate ? isFocused : focusableDate && isEqual(focusableDate, day.date));
  return /*#__PURE__*/_jsxs(Box, _objectSpread(_objectSpread({
    __css: styles['cell'],
    onClick: day !== null && day !== void 0 && day.isDisabled ? undefined : day.selectDay,
    "aria-selected": day.isSelected,
    "data-secondary-selected-date": day.isSecondarySelectedDate,
    "aria-disabled": day.isDisabled,
    "data-testid": "calendar-day-".concat(formatDate(day.date, {
      day: 'numeric'
    })),
    tabIndex: isFocusableDate ? 0 : -1
  }, props), {}, {
    ref: ref,
    onKeyDown: onKeyDown,
    onFocus: onFocus,
    onBlur: onBlur
    /* The following props are used for accessibility purposes */
    // Used to indicate the current date. (associated with gridcell role)
    ,
    "aria-current": isToday(day.date) && 'date' || day.isSelected && 'true' || undefined,
    role: "gridcell" // ts-ignore is used due to `type` issues with `<Box/>`.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    ,
    type: "button",
    "aria-colindex": ariaColIndex,
    "data-timestamp": day.date.getTime(),
    children: [/*#__PURE__*/_jsx(Box // Used for reading the date in a "1st of January 2022" format.
    , {
      role: "time",
      "aria-label": format(day.date, 'do MMMM yyyy'),
      children: day.label
    }), showToday && isToday(day.date) && /*#__PURE__*/_jsx(Box, {
      "data-testid": "today-marker",
      __css: styles['todayMarker']
    })]
  }));
});
//# sourceMappingURL=Day.js.map